/**
 * Generic admin report exporters (PDF / XLSX).
 * columns: [{ key, label, width? }] — width used for PDF column sizing
 */

const dateStamp = () => new Date().toISOString().split('T')[0];

const cellValue = (row, key) => {
  if (!row) return '';
  const value = typeof key === 'function' ? key(row) : row[key];
  if (value === null || value === undefined) return '';
  return String(value);
};

export const exportReportToExcel = async (rows, columns, { filename, sheetName = 'Report' } = {}) => {
  if (!rows?.length) {
    throw new Error('No data to export');
  }

  const XLSX = await import('xlsx');
  const worksheetData = rows.map((row) => {
    const out = {};
    columns.forEach((col) => {
      out[col.label] = cellValue(row, col.key);
    });
    return out;
  });

  const worksheet = XLSX.utils.json_to_sheet(worksheetData);
  const workbook = XLSX.utils.book_new();
  XLSX.utils.book_append_sheet(workbook, worksheet, sheetName.slice(0, 31));
  XLSX.writeFile(workbook, `${filename || 'report'}-${dateStamp()}.xlsx`);
};

export const exportReportToPDF = async (rows, columns, { title = 'Report', filename = 'report' } = {}) => {
  if (!rows?.length) {
    throw new Error('No data to export');
  }

  const { jsPDF } = await import('jspdf');
  const doc = new jsPDF({ orientation: 'landscape' });
  const pageWidth = doc.internal.pageSize.getWidth();
  const pageHeight = doc.internal.pageSize.getHeight();
  const margin = 12;
  const usableWidth = pageWidth - margin * 2;

  const totalWeight = columns.reduce((sum, col) => sum + (col.width || 1), 0);
  const colWidths = columns.map((col) => ((col.width || 1) / totalWeight) * usableWidth);

  let currentY = margin + 6;

  doc.setFontSize(16);
  doc.setFont('helvetica', 'bold');
  doc.text(title, margin, currentY);
  currentY += 7;

  doc.setFontSize(9);
  doc.setFont('helvetica', 'normal');
  doc.text(`Generated: ${new Date().toLocaleString()}`, margin, currentY);
  doc.text(`Records: ${rows.length}`, margin + 70, currentY);
  currentY += 8;

  const drawHeader = () => {
    doc.setFontSize(8);
    doc.setFont('helvetica', 'bold');
    let x = margin;
    columns.forEach((col, index) => {
      doc.setFillColor(28, 100, 242);
      doc.rect(x, currentY - 4, colWidths[index], 7, 'F');
      doc.setTextColor(255, 255, 255);
      doc.text(String(col.label).substring(0, 28), x + 1.5, currentY);
      x += colWidths[index];
    });
    doc.setTextColor(0, 0, 0);
    currentY += 8;
    doc.setFont('helvetica', 'normal');
  };

  drawHeader();

  rows.forEach((row, rowIndex) => {
    if (currentY > pageHeight - 14) {
      doc.addPage();
      currentY = margin + 6;
      drawHeader();
    }

    if (rowIndex % 2 === 0) {
      doc.setFillColor(243, 244, 246);
      doc.rect(margin, currentY - 4, usableWidth, 6.5, 'F');
    }

    let x = margin;
    columns.forEach((col, index) => {
      const text = cellValue(row, col.key).substring(0, Math.max(8, Math.floor(colWidths[index] / 2.2)));
      doc.text(text, x + 1.5, currentY);
      x += colWidths[index];
    });
    currentY += 6.5;
  });

  doc.save(`${filename}-${dateStamp()}.pdf`);
};
